// Calendar view.
//
// Upstream renders this with @fullcalendar (fullCalendar.tsx). A calendar
// library is a large dependency for a single view, so this is a hand-rolled
// month grid with the same affordances we need: month navigation, "Today",
// per-day cards from the view's date property, drag-to-reschedule and
// click-to-create. It does not implement week/day/time-grid views.

import {ReactElement, DragEvent, useMemo, useState} from 'react'
import classnames from 'classnames'
import {Board} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import * as mutator from '../../state/mutator'
import {createDatePropertyFromString, encodeDateProperty} from '../../state/properties'
import {Utils} from '../../common/utils'
import {Icon} from '../common/icons'

type Props = {
    board: Board
    view: BoardView
    cards: Card[]
    usersById: Record<string, IUser>
    readOnly: boolean
    onCardClicked: (cardId: string) => void
}

const MS_PER_DAY = 86400000

// Chinese calendars start the week on Monday.
const locale = Utils.getLocale()
const weekdayFormat = new Intl.DateTimeFormat(locale, {weekday: 'short'})
const monthFormat = new Intl.DateTimeFormat(locale, {month: 'long', year: 'numeric'})
const cellFormat = new Intl.DateTimeFormat(locale, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})

function startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function monthMatrix(anchor: Date): Date[][] {
    const first = startOfDay(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
    const offset = (first.getDay() + 6) % 7
    const gridStart = new Date(first.getTime() - offset * MS_PER_DAY)
    const weeks: Date[][] = []
    for (let w = 0; w < 6; w++) {
        const row: Date[] = []
        for (let d = 0; d < 7; d++) {
            row.push(new Date(gridStart.getTime() + (w * 7 + d) * MS_PER_DAY))
        }
        weeks.push(row)
    }
    return weeks
}

function dateOf(card: Card, propertyId: string | undefined): Date | undefined {
    if (!propertyId) {
        return undefined
    }
    const dp = createDatePropertyFromString(card.fields.properties[propertyId])
    const ms = dp.from ?? dp.to
    return ms === undefined ? undefined : startOfDay(new Date(ms))
}

const sameDay = (a: Date | undefined, b: Date): boolean => Boolean(a) && a!.getTime() === b.getTime()

export default function Calendar({board, view, cards, readOnly, onCardClicked}: Props): ReactElement {
    const propertyId = view.fields.dateDisplayPropertyId || board.cardProperties.find((p) => p.type === 'date')?.id
    const template = board.cardProperties.find((p) => p.id === propertyId)
    const [anchor, setAnchor] = useState<Date>(() => new Date())
    const [draggingCardId, setDraggingCardId] = useState<string | null>(null)

    const weeks = useMemo(() => monthMatrix(anchor), [anchor])
    const today = startOfDay(new Date()).getTime()
    const weekdayLabels = useMemo(() => {
        const base = new Date(2024, 0, 1) // a Monday
        return Array.from({length: 7}, (_, i) => weekdayFormat.format(new Date(base.getTime() + i * MS_PER_DAY)))
    }, [])

    const move = (days: number): void => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + days, 1))

    const dropOnDay = async (event: DragEvent<HTMLDivElement>, day: Date): Promise<void> => {
        event.preventDefault()
        const cardId = draggingCardId || event.dataTransfer.getData('text/plain')
        setDraggingCardId(null)
        const card = cards.find((c) => c.id === cardId)
        if (!card || !propertyId || readOnly) {
            return
        }
        const existing = createDatePropertyFromString(card.fields.properties[propertyId])
        const duration = existing.to && existing.from ? existing.to - existing.from : 0
        const from = startOfDay(day).getTime()
        await mutator.changeCardFieldValue(board.id, card, propertyId, encodeDateProperty({from, to: duration ? from + duration : undefined}))
    }

    const addOnDay = async (day: Date): Promise<void> => {
        if (!propertyId) {
            return
        }
        const card = await mutator.createCard(board, view, {
            title: '未命名',
            properties: {[propertyId]: encodeDateProperty({from: startOfDay(day).getTime()})},
        })
        onCardClicked(card.id)
    }

    return (
        <div className='Calendar flex flex-1 flex-col overflow-hidden pb-6'>
            <div className='mb-3 flex items-center gap-3'>
                <div className='font-semibold'>
                    {monthFormat.format(anchor)}
                </div>
                {template && (
                    <span className='flex items-center gap-1 text-xs opacity-60'>
                        <Icon name='date' size={13}/>
                        {template.name}
                    </span>
                )}
                <div className='ml-auto flex items-center gap-1'>
                    <button type='button' className='Button Button--text' onClick={() => setAnchor(new Date())}>今天</button>
                    <button type='button' className='IconButton' onClick={() => move(-1)} aria-label='上个月'><Icon name='chevron-left' size={16}/></button>
                    <button type='button' className='IconButton' onClick={() => move(1)} aria-label='下个月'><Icon name='chevron-right' size={16}/></button>
                </div>
            </div>

            {!propertyId && (
                <div className='mb-2 rounded bg-[#fff3cd] px-3 py-2 text-xs text-[#664d03]'>
                    该视图没有可用的日期属性，请在视图工具栏选择「日历展示依据」。
                </div>
            )}

            <div className='grid shrink-0 grid-cols-7 text-xxs uppercase opacity-60' role='row'>
                {weekdayLabels.map((label) => <div key={label} className='px-2 py-1' role='columnheader'>{label}</div>)}
            </div>

            <div className='grid flex-1 grid-cols-7 gap-px overflow-auto bg-main-fg/10' role='grid'>
                {weeks.flat().map((day) => {
                    const dayCards = cards.filter((card) => sameDay(dateOf(card, propertyId), day))
                    const inMonth = day.getMonth() === anchor.getMonth()
                    const isToday = day.getTime() === today
                    return (
                        <div
                            key={day.toISOString()}
                            role='gridcell'
                            aria-label={cellFormat.format(day)}
                            className={classnames('min-h-[104px] bg-main-bg p-1.5', !inMonth && 'opacity-40')}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => void dropOnDay(event, day)}
                        >
                            <div className='mb-1 flex items-center justify-between'>
                                <span
                                    className={classnames(
                                        'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full text-xs',
                                        isToday && 'bg-button-bg font-semibold text-button-fg',
                                    )}
                                >
                                    {day.getDate()}
                                </span>
                                {!readOnly && dayCards.length > 0 && (
                                    <button
                                        type='button'
                                        className='IconButton h-5 w-5 opacity-0 hover:opacity-100'
                                        onClick={() => addOnDay(day)}
                                        aria-label='在这一天新建卡片'
                                    >
                                        <Icon name='add' size={13}/>
                                    </button>
                                )}
                            </div>
                            {dayCards.map((card) => (
                                <div
                                    key={card.id}
                                    draggable={!readOnly}
                                    onDragStart={(event) => {
                                        event.dataTransfer.setData('text/plain', card.id)
                                        setDraggingCardId(card.id)
                                    }}
                                    onDragEnd={() => setDraggingCardId(null)}
                                    onClick={() => onCardClicked(card.id)}
                                    className={classnames('mb-1 truncate rounded px-1.5 py-1 text-xs hover:bg-main-fg/10', draggingCardId === card.id && 'opacity-40')}
                                    style={{backgroundColor: 'rgba(63,67,80,.07)'}}
                                    role='button'
                                    tabIndex={0}
                                    onKeyDown={(event) => event.key === 'Enter' && onCardClicked(card.id)}
                                    title={card.title}
                                >
                                    {card.fields.icon && <span className='mr-1' aria-hidden={true}>{card.fields.icon}</span>}
                                    {card.title || '未命名'}
                                </div>
                            ))}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
